import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  GraduationCap,
  Loader2,
  ShieldCheck,
  TrainFront,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../../../../axios";

const getExamId = (exam, index) => exam?._id || exam?.id || exam?.examId || index;

const getExamName = (exam) =>
  exam?.examName || exam?.name || exam?.title || exam?.category || "Exam";

const getExamIcon = (examName = "") => {
  const name = examName.toLowerCase();

  if (name.includes("bank")) return Building2;
  if (name.includes("rail")) return TrainFront;
  if (name.includes("psc") || name.includes("cgpsc")) return BriefcaseBusiness;
  if (name.includes("teach")) return BookOpen;
  if (name.includes("defence") || name.includes("defense")) return ShieldCheck;

  return GraduationCap;
};

const SelectExam = () => {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [selectedExamIds, setSelectedExamIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let savedIds = [];

    try {
      savedIds = JSON.parse(localStorage.getItem("selectedExamIds") || "[]");
    } catch {
      savedIds = [];
    }

    if (Array.isArray(savedIds)) {
      setSelectedExamIds(savedIds.map(String));
    }
  }, []);

  useEffect(() => {
    loadUserAndExams();
  }, []);

  const loadUserAndExams = async () => {
    try {
      const meResponse = await axios.get("me");
      const freshUser = meResponse?.data?.result || {};
      const selectedState =
        freshUser?.selectedState?._id ||
        freshUser?.selectedState ||
        localStorage.getItem("stateId");

      const res = await axios.get(
        selectedState ? `/exams/all?stateId=${selectedState}` : "/exams/all"
      );
      const examList = res.data?.result || res.data?.data || res.data || [];

      setExams(Array.isArray(examList) ? examList : []);
    } catch (err) {
      toast.error("Unable to load exams");
    } finally {
      setLoading(false);
    }
  };

  const selectedExams = useMemo(
    () =>
      exams.filter((exam, index) =>
        selectedExamIds.includes(String(getExamId(exam, index)))
      ),
    [exams, selectedExamIds]
  );

  const toggleExam = (exam, index) => {
    const examId = String(getExamId(exam, index));

    setSelectedExamIds((prev) =>
      prev.includes(examId)
        ? prev.filter((id) => id !== examId)
        : [...prev, examId]
    );
  };

  const handleContinue = async () => {
    if (!selectedExamIds.length) {
      toast.error("Please select at least one exam");
      return;
    }

    const primaryExamId = selectedExamIds[0];

    try {
      setSaving(true);

      await axios.post("/userUpdate", {
        selectedExam: primaryExamId,
        exam: primaryExamId,
        selectedExams: selectedExamIds,
        examSelected: true,
      });

      localStorage.setItem("selectedExamIds", JSON.stringify(selectedExamIds));
      localStorage.setItem(
        "selectedExamNames",
        JSON.stringify(selectedExams.map(getExamName))
      );

      if (selectedExams[0]) {
        localStorage.setItem("examId", String(getExamId(selectedExams[0], 0)));
        localStorage.setItem("examName", getExamName(selectedExams[0]));
      }

      navigate("/dash");
    } catch (err) {
      toast.error("Unable to update exam selection");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto bg-[#fbfbfb] text-[#552062]">
      <div className="pointer-events-none fixed right-[-145px] top-[-92px] h-[395px] w-[395px] rounded-full bg-[#f3def5] md:right-[-90px] md:top-[-150px] md:h-[520px] md:w-[520px]" />
      {/* <div className="pointer-events-none fixed right-[78px] top-[-20px] h-[420px] w-[280px] rounded-b-full bg-[#f8eafb]" /> */}

      <main className="relative z-10 mx-auto flex min-h-[100dvh] w-full max-w-[760px] flex-col px-5 pb-28 pt-14 sm:px-8 md:max-w-[1000px] md:pb-32 md:pt-12 lg:px-8">
        <section className="text-left md:flex md:items-end md:justify-between md:gap-8">
          <div className="md:max-w-[680px]">
            <h1 className="text-[2.45rem] font-bold leading-tight text-[#552062] sm:text-3xl md:text-4xl">
              Select Exams
            </h1>
            <p className="mt-1 max-w-[640px] text-[1.15rem] font-medium leading-8 text-[#64606e] sm:text-xl">
              Choose one or more exams you are preparing for to personalize your
              dashboard.
            </p>
          </div>

          <div className="mt-3 inline-flex min-h-12 shrink-0 items-center rounded-full bg-[#f0edf2] px-6 text-base font-semibold text-[#5b2766] shadow-sm md:mb-1">
            {selectedExamIds.length} selected
          </div>
        </section>

        <section className="mt-5 grid flex-1 grid-cols-1 content-start gap-4 md:mt-7 md:grid-cols-2 md:gap-4 lg:grid-cols-3">
          {loading && (
            <div className="flex min-h-[260px] items-center justify-center md:col-span-2 lg:col-span-3">
              <Loader2 className="animate-spin text-[#5b2766]" size={34} />
            </div>
          )}

          {!loading &&
            exams.map((exam, index) => {
              const examId = String(getExamId(exam, index));
              const examName = getExamName(exam);
              const Icon = getExamIcon(examName);
              const isSelected = selectedExamIds.includes(examId);

              return (
                <button
                  key={examId}
                  type="button"
                  onClick={() => toggleExam(exam, index)}
                  className={`flex min-h-[6.25rem] w-full items-center gap-5 rounded-[1.6rem] border bg-white px-4 py-4 text-left shadow-[0_8px_18px_rgba(38,35,44,0.16)] transition md:min-h-[7.35rem] md:items-start md:gap-4 md:px-4 md:py-5 hover:md:-translate-y-0.5 hover:md:shadow-[0_14px_26px_rgba(38,35,44,0.15)] ${
                    isSelected
                      ? "border-[#5b2766] ring-2 ring-[#ead9ef]"
                      : "border-[#e9e3ed]"
                  }`}
                >
                  <span className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-xl bg-[#f5eafa] text-[#5b2766] md:h-12 md:w-12">
                    <Icon size={30} strokeWidth={1.0} />
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-[1.45rem] font-extrabold leading-tight text-[#552062] sm:text-2xl md:text-[1.35rem]">
                      {examName}
                    </span>
                    <span className="mt-2 block text-base font-medium text-[#6f6a75] sm:text-lg md:text-base">
                      Select this exam
                    </span>
                  </span>

                  <span
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 md:h-10 md:w-10 ${
                      isSelected
                        ? "border-[#5b2766] bg-[#5b2766]"
                        : "border-[#cbbbd2] bg-white"
                    }`}
                    aria-hidden="true"
                  >
                    {isSelected && (
                      <span className="h-4 w-4 rounded-full bg-white" />
                    )}
                  </span>
                </button>
              );
            })}

          {!loading && !exams.length && (
            <div className="rounded-[1.6rem] border border-[#e9e3ed] bg-white px-5 py-8 text-center text-[#6f6a75] shadow-sm md:col-span-2 lg:col-span-3">
              No exams available right now.
            </div>
          )}
        </section>
      </main>

      <div className="fixed inset-x-0 bottom-0 z-[130] mx-auto w-full max-w-[760px] px-5 pb-5 sm:px-8 md:max-w-[1000px] md:px-8">
        <button
          type="button"
          onClick={handleContinue}
          disabled={!selectedExamIds.length || saving}
          className="ml-auto flex h-16 w-full items-center justify-center gap-3 rounded-[1.55rem] bg-[#552062] text-lg font-bold text-white shadow-[0_-6px_22px_rgba(85,32,98,0.25)] transition disabled:cursor-not-allowed disabled:opacity-55 sm:max-w-[360px]"
        >
          {saving ? (
            <>
              <Loader2 className="animate-spin" size={22} />
              Saving...
            </>
          ) : (
            <>
              Continue
              <ArrowRight size={24} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SelectExam;
